/**
 * ShareToChat — bouton + modal réutilisables pour partager un objet métier
 * (signal de veille, risque, fiche annuaire) dans une conversation du Chat interne.
 * Style Bloomberg : l'objet devient une carte cliquable dans la discussion.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { MessageSquareShare, X, Loader2, Hash, Users, Send, Check } from "lucide-react";

const KIND_LABELS = { signal: "ce signal", risque: "ce risque", annuaire: "cette fiche" };

function convName(c, meEmail) {
  const type = c.type || "channel";
  if (type === "dm") {
    const other = (c.member_emails || []).find((e) => e !== meEmail);
    return (c.member_names || {})[other] || other || "Conversation";
  }
  if (type === "group") return c.name;
  return `#${c.name}`;
}

export function ShareToChatButton({ attachment, title, compact = true, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={className || (compact
          ? "p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-[#1D9E75] transition"
          : "btn-secondary text-xs")}
        title="Partager dans le chat"
        data-testid={`share-chat-btn-${attachment.kind}-${attachment.id}`}
      >
        <MessageSquareShare className={compact ? "w-3.5 h-3.5" : "w-3.5 h-3.5"} />
        {!compact && " Chat"}
      </button>
      {open && (
        <ShareToChatModal attachment={attachment} title={title} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function ShareToChatModal({ attachment, title, onClose }) {
  const [convs, setConvs] = useState(null);
  const [me, setMe] = useState(null);
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/workspace/chat-interne/channels")
      .then((r) => { setConvs(r.data.items || []); setMe(r.data.me || null); })
      .catch((e) => notify.error(fmtError(e)));
  }, []);

  const send = async () => {
    if (!selected || sending) return;
    setSending(true);
    try {
      const text = comment.trim() || `📎 Partage : ${title || KIND_LABELS[attachment.kind] || "un élément"}`;
      await api.post(`/workspace/chat-interne/channels/${selected}/messages`, {
        text,
        attachment: { kind: attachment.kind, id: attachment.id },
      });
      notify.success("Partagé dans le chat.");
      onClose();
    } catch (e) {
      notify.error(fmtError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="w-full max-w-md bg-[#0A0A0A] border border-white/10 rounded-lg p-5 text-white" onClick={(e) => e.stopPropagation()} data-testid="share-chat-modal">
        <div className="flex items-center justify-between mb-1">
          <div className="text-sm font-semibold flex items-center gap-2">
            <MessageSquareShare className="w-4 h-4 text-[#1D9E75]" /> Partager dans le chat
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="share-chat-close"><X className="w-4 h-4" /></button>
        </div>
        {title && <div className="text-[11px] text-zinc-500 mb-3 truncate">« {title} »</div>}
        <div className="max-h-56 overflow-y-auto space-y-0.5 mb-3 border border-white/5 rounded-md p-1.5" data-testid="share-chat-conv-list">
          {convs === null ? (
            <div className="text-xs text-zinc-500 py-3 text-center flex items-center justify-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement…</div>
          ) : convs.length === 0 ? (
            <div className="text-xs text-zinc-500 py-3 text-center">Aucune conversation.</div>
          ) : convs.map((c) => {
            const type = c.type || "channel";
            return (
              <button
                key={c.id}
                onClick={() => setSelected(c.id)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-xs transition ${selected === c.id ? "bg-[#1D9E75]/15 border border-[#1D9E75]/40 text-white" : "hover:bg-white/5 border border-transparent text-zinc-300"}`}
                data-testid={`share-chat-conv-${c.id}`}
              >
                {type === "channel" ? <Hash className="w-3.5 h-3.5 text-zinc-500 shrink-0" /> : <Users className="w-3.5 h-3.5 text-zinc-500 shrink-0" />}
                <span className="flex-1 truncate">{convName(c, me?.email)}</span>
                {selected === c.id && <Check className="w-3.5 h-3.5 text-[#1D9E75]" />}
              </button>
            );
          })}
        </div>
        <input
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder="Ajouter un commentaire (optionnel)…"
          maxLength={500}
          className="w-full bg-black/50 border border-white/10 rounded px-3 py-2 text-sm text-white mb-3 focus:outline-none focus:border-[#1D9E75]"
          data-testid="share-chat-comment"
        />
        <button
          onClick={send}
          disabled={!selected || sending}
          className="w-full btn-primary text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50 py-2"
          data-testid="share-chat-send"
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Partager
        </button>
      </div>
    </div>
  );
}
